import React, { useState, useEffect } from 'react';
import { getOlsRunwayParameters } from '../api/client';

const exportToCsv = (rows, filename) => {
    const csvContent = rows.map(row => row.map(val => {
        const cleanVal = String(val ?? '').replace(/—/g, '-');
        return `"${cleanVal.replace(/"/g, '""')}"`;
    }).join(",")).join("\n");
    // Prepend a UTF-8 BOM — without it, Excel/Numbers on Mac guesses MacRoman
    // for a plain CSV and mangles non-ASCII characters like the DMS "°" sign.
    const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

// Row layout mirrors "ols parameters.xlsx" exactly: two side-by-side blocks
// (left = RWY-d1 details + Strip/Transitional/IHS/Conical, right = RWY-d2
// details + Approach/Take-off Climb), each block a label column plus one
// value column per threshold.
function buildSheetRows(data) {
    const rows = [];
    data.runway_pairs.forEach(pair => {
        const [t1, t2] = pair.thresholds;
        rows.push([`${data.ad_name} (${data.icao})`]);
        rows.push([`RUNWAY ${pair.pair}`]);
        rows.push([]);
        rows.push([`RUNWAY DETAILS: RWY ${t1.threshold}`, '', '', `RUNWAY DETAILS: RWY ${t2.threshold}`]);
        rows.push(['Threshold', t1.threshold, '', 'Threshold', t2.threshold]);
        rows.push(['Classification', t1.classification, '', 'Classification', t2.classification]);
        rows.push(['Code', t1.code, '', 'Code', t2.code]);
        rows.push(['Latitude', t1.latitude, '', 'Latitude', t2.latitude]);
        rows.push(['Longitude', t1.longitude, '', 'Longitude', t2.longitude]);
        rows.push(['Elevation (m)', `${t1.elev_m} m`, '', 'Elevation (m)', `${t2.elev_m} m`]);
        rows.push(['Elevation (FT)', `${t1.elev_ft} FT`, '', 'Elevation (FT)', `${t2.elev_ft} FT`]);
        rows.push(['Displaced THR', `${t1.displaced_thr_m} m`, '', 'Displaced THR', `${t2.displaced_thr_m} m`]);
        rows.push(['Stopway', `${t1.stopway_m} m`, '', 'Stopway', `${t2.stopway_m} m`]);
        rows.push(['Clearway', `${t1.clearway_m} m`, '', 'Clearway', `${t2.clearway_m} m`]);
        rows.push(['AD Ref Elev', `${t1.ad_ref_elev_m} m | ${t1.ad_ref_elev_ft} FT`, '', 'AD Ref Elev', `${t2.ad_ref_elev_m} m | ${t2.ad_ref_elev_ft} FT`]);
        rows.push([]);

        rows.push(['Runway Strip', t1.threshold, t2.threshold, 'Approach Surface', t1.threshold, t2.threshold]);
        rows.push(['Length', pair.strip.length_m, pair.strip.length_m, 'Length of Inner Edge', pair.approach.inner_edge_m, pair.approach.inner_edge_m]);
        rows.push(['Width', pair.strip.width_m, pair.strip.width_m, 'Dist from Threshold', pair.approach.dist_from_thr_m, pair.approach.dist_from_thr_m]);
        rows.push(['Total Rwy Strip Length', pair.strip.total_rwy_strip_length_m, '', 'Divergence', pair.approach.divergence, pair.approach.divergence]);
        rows.push(['Transitional Surface', '', '', '1st Section Length', pair.approach.sec1_length_m, pair.approach.sec1_length_m]);
        rows.push(['Slope', pair.transitional.slope, pair.transitional.slope, '1st Section Slope', pair.approach.sec1_slope, pair.approach.sec1_slope]);
        rows.push(['Inner Horizontal Surface', '', '', '2nd Section Length', pair.approach.sec2_length_m, pair.approach.sec2_length_m]);
        rows.push(['Height', pair.inner_horizontal.height_m, pair.inner_horizontal.height_m, '2nd Section Slope', pair.approach.sec2_slope, pair.approach.sec2_slope]);
        rows.push(['Radius', pair.inner_horizontal.radius_m, pair.inner_horizontal.radius_m, 'Horizontal Section Length', pair.approach.horizontal_length_m, pair.approach.horizontal_length_m]);
        rows.push(['Conical Surface', '', '', 'Take-off Climb Surface']);
        rows.push(['Slope', pair.conical.slope, pair.conical.slope, 'Length of Inner Edge', pair.takeoff_climb.inner_edge_m, pair.takeoff_climb.inner_edge_m]);
        rows.push(['Height', pair.conical.height_m, pair.conical.height_m, 'Dist from Runway End', pair.takeoff_climb.dist_from_rwy_end_m, pair.takeoff_climb.dist_from_rwy_end_m]);
        rows.push(['', '', '', 'Divergence', pair.takeoff_climb.divergence, pair.takeoff_climb.divergence]);
        rows.push(['', '', '', 'Final Width', pair.takeoff_climb.final_width_m, pair.takeoff_climb.final_width_m]);
        rows.push(['', '', '', 'Length', pair.takeoff_climb.length_m, pair.takeoff_climb.length_m]);
        rows.push(['', '', '', 'Slope', pair.takeoff_climb.slope, pair.takeoff_climb.slope]);
        rows.push([]);
        rows.push([]);
    });
    return rows;
}

const CELL_BORDER = '1px solid var(--glass-border)';
const TABLE_BOX = { width: '100%', borderCollapse: 'collapse', fontSize: 11, border: CELL_BORDER };
const CELL = { padding: '3px 6px', border: CELL_BORDER };

function KV({ label, value }) {
    return (
        <tr>
            <td style={{ ...CELL, color: 'var(--text-tertiary)', width: '50%' }}>{label}</td>
            <td style={{ ...CELL, color: 'var(--text-primary)' }}>{value}</td>
        </tr>
    );
}

function TwoCol({ label, v1, v2 }) {
    return (
        <tr>
            <td style={{ ...CELL, color: 'var(--text-tertiary)', width: '50%' }}>{label}</td>
            <td style={{ ...CELL, color: 'var(--text-primary)' }}>{v1}</td>
            <td style={{ ...CELL, color: 'var(--text-primary)' }}>{v2}</td>
        </tr>
    );
}

function SectionHeader({ children, cols = 1 }) {
    return (
        <tr>
            <td colSpan={cols + 1} style={{ ...CELL, color: 'var(--text-blue)', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', background: 'var(--blue-glass-fill)' }}>
                {children}
            </td>
        </tr>
    );
}

function ThrDetails({ t }) {
    return (
        <table style={{ ...TABLE_BOX, marginBottom: 8 }}>
            <tbody>
                <SectionHeader>RUNWAY DETAILS: RWY {t.threshold}</SectionHeader>
                <KV label="Threshold" value={t.threshold} />
                <KV label="Classification" value={t.classification} />
                <KV label="Code" value={t.code} />
                <KV label="Latitude" value={t.latitude} />
                <KV label="Longitude" value={t.longitude} />
                <KV label="Elevation (m)" value={`${t.elev_m} m`} />
                <KV label="Elevation (FT)" value={`${t.elev_ft} FT`} />
                <KV label="Displaced THR" value={`${t.displaced_thr_m} m`} />
                <KV label="Stopway" value={`${t.stopway_m} m`} />
                <KV label="Clearway" value={`${t.clearway_m} m`} />
                <KV label="AD Ref Elev" value={`${t.ad_ref_elev_m} m | ${t.ad_ref_elev_ft} FT`} />
            </tbody>
        </table>
    );
}

export default function OlsParametersModal({ onClose, arps = [] }) {
    const [selectedIcao, setSelectedIcao] = useState(arps[0]?.icao || '');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!selectedIcao) return;
        setLoading(true);
        setError(null);
        getOlsRunwayParameters(selectedIcao)
            .then(res => setData(res.data))
            .catch(err => {
                console.error('Failed to load OLS parameters:', err);
                setError(err.response?.data?.detail || 'Failed to load OLS parameters.');
                setData(null);
            })
            .finally(() => setLoading(false));
    }, [selectedIcao]);

    const handleExportCsv = () => {
        if (!data) return;
        exportToCsv(buildSheetRows(data), `${data.icao}_OLS_parameters.csv`);
    };

    return (
        <div className="dg-modal-overlay">
            <div className="dg-modal" style={{ padding: 32, width: 860, maxHeight: '85vh', overflowY: 'auto' }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>
                    OLS Parameters
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 20 }}>
                    The Obstacle Limitation Surface parameters currently applied to each runway, as used by the
                    assessment engine.
                </div>

                {arps.length > 1 && (
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
                            Aerodrome
                        </div>
                        <select className="dg-input" value={selectedIcao} onChange={e => setSelectedIcao(e.target.value)}>
                            {arps.map(a => (
                                <option key={a.icao} value={a.icao}>{a.icao} — {a.name}</option>
                            ))}
                        </select>
                    </div>
                )}

                {loading && (
                    <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-tertiary)' }}>Loading…</div>
                )}
                {error && (
                    <div style={{ padding: 12, color: '#ef4444', fontSize: 12 }}>{error}</div>
                )}

                {data && !loading && data.runway_pairs.map(pair => {
                    const [t1, t2] = pair.thresholds;
                    return (
                        <div key={pair.pair} style={{ marginBottom: 24 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>
                                {data.ad_name} ({data.icao}) — RUNWAY {pair.pair}
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                {/* ── Left column: RWY-t1 details, then Strip/Transitional/IHS/Conical ── */}
                                <div>
                                    <ThrDetails t={t1} />
                                    <table style={TABLE_BOX}>
                                        <thead>
                                            <tr>
                                                <th style={{ ...CELL, textAlign: 'left', color: 'var(--text-tertiary)' }}></th>
                                                <th style={{ ...CELL, textAlign: 'left', color: 'var(--text-tertiary)' }}>{t1.threshold}</th>
                                                <th style={{ ...CELL, textAlign: 'left', color: 'var(--text-tertiary)' }}>{t2.threshold}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <SectionHeader cols={2}>Runway Strip</SectionHeader>
                                            <TwoCol label="Length" v1={pair.strip.length_m} v2={pair.strip.length_m} />
                                            <TwoCol label="Width" v1={pair.strip.width_m} v2={pair.strip.width_m} />
                                            <TwoCol label="Total Rwy Strip Length" v1={pair.strip.total_rwy_strip_length_m} v2="" />
                                            <SectionHeader cols={2}>Transitional Surface</SectionHeader>
                                            <TwoCol label="Slope" v1={pair.transitional.slope} v2={pair.transitional.slope} />
                                            <SectionHeader cols={2}>Inner Horizontal Surface</SectionHeader>
                                            <TwoCol label="Height" v1={pair.inner_horizontal.height_m} v2={pair.inner_horizontal.height_m} />
                                            <TwoCol label="Radius" v1={pair.inner_horizontal.radius_m} v2={pair.inner_horizontal.radius_m} />
                                            <SectionHeader cols={2}>Conical Surface</SectionHeader>
                                            <TwoCol label="Slope" v1={pair.conical.slope} v2={pair.conical.slope} />
                                            <TwoCol label="Height" v1={pair.conical.height_m} v2={pair.conical.height_m} />
                                        </tbody>
                                    </table>
                                </div>

                                {/* ── Right column: RWY-t2 details, then Approach/Take-off Climb ── */}
                                <div>
                                    <ThrDetails t={t2} />
                                    <table style={TABLE_BOX}>
                                        <thead>
                                            <tr>
                                                <th style={{ ...CELL, textAlign: 'left', color: 'var(--text-tertiary)' }}></th>
                                                <th style={{ ...CELL, textAlign: 'left', color: 'var(--text-tertiary)' }}>{t1.threshold}</th>
                                                <th style={{ ...CELL, textAlign: 'left', color: 'var(--text-tertiary)' }}>{t2.threshold}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <SectionHeader cols={2}>Approach Surface</SectionHeader>
                                            <TwoCol label="Length of Inner Edge" v1={pair.approach.inner_edge_m} v2={pair.approach.inner_edge_m} />
                                            <TwoCol label="Dist from Threshold" v1={pair.approach.dist_from_thr_m} v2={pair.approach.dist_from_thr_m} />
                                            <TwoCol label="Divergence" v1={pair.approach.divergence} v2={pair.approach.divergence} />
                                            <TwoCol label="1st Section Length" v1={pair.approach.sec1_length_m} v2={pair.approach.sec1_length_m} />
                                            <TwoCol label="1st Section Slope" v1={pair.approach.sec1_slope} v2={pair.approach.sec1_slope} />
                                            <TwoCol label="2nd Section Length" v1={pair.approach.sec2_length_m} v2={pair.approach.sec2_length_m} />
                                            <TwoCol label="2nd Section Slope" v1={pair.approach.sec2_slope} v2={pair.approach.sec2_slope} />
                                            <TwoCol label="Horizontal Section Length" v1={pair.approach.horizontal_length_m} v2={pair.approach.horizontal_length_m} />
                                            <SectionHeader cols={2}>Take-off Climb Surface</SectionHeader>
                                            <TwoCol label="Length of Inner Edge" v1={pair.takeoff_climb.inner_edge_m} v2={pair.takeoff_climb.inner_edge_m} />
                                            <TwoCol label="Dist from Runway End" v1={pair.takeoff_climb.dist_from_rwy_end_m} v2={pair.takeoff_climb.dist_from_rwy_end_m} />
                                            <TwoCol label="Divergence" v1={pair.takeoff_climb.divergence} v2={pair.takeoff_climb.divergence} />
                                            <TwoCol label="Final Width" v1={pair.takeoff_climb.final_width_m} v2={pair.takeoff_climb.final_width_m} />
                                            <TwoCol label="Length" v1={pair.takeoff_climb.length_m} v2={pair.takeoff_climb.length_m} />
                                            <TwoCol label="Slope" v1={pair.takeoff_climb.slope} v2={pair.takeoff_climb.slope} />
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    );
                })}

                <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end' }}>
                    <button className="dg-btn dg-btn-ghost" onClick={onClose}>Close</button>
                    <button
                        className="dg-btn dg-btn-primary"
                        onClick={handleExportCsv}
                        disabled={!data}
                        style={{ opacity: !data ? 0.5 : 1 }}
                    >
                        Export CSV
                    </button>
                </div>
            </div>
        </div>
    );
}
